// PostgreSQL veritabanı servisleri
// Backend API endpoint'i kullanarak veritabanına erişim

// PostgreSQL veritabanı servisleri
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Backend API'den şemaları listele
 */
export const listSchemas = async () => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil. Lütfen .env.local dosyasına REACT_APP_API_BASE_URL ekleyin.');
    }

    const response = await fetch(`${API_BASE_URL}/api/database/schemas`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response'un JSON olup olmadığını kontrol et
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API HTML döndü (backend çalışmıyor olabilir):', text.substring(0, 200));
      throw new Error(`Backend API çalışmıyor. Lütfen backend'i başlatın: cd backend && npm start`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.schemas || [];
  } catch (error) {
    console.error('❌ Şema listesi hatası:', error);
    throw error;
  }
};

/**
 * Backend API'den belirli bir şemadaki tabloları listele
 */
export const listTables = async (schema = 'public') => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil. Lütfen .env.local dosyasına REACT_APP_API_BASE_URL ekleyin.');
    }

    const response = await fetch(`${API_BASE_URL}/api/database/tables?schema=${encodeURIComponent(schema)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response'un JSON olup olmadığını kontrol et
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API HTML döndü (backend çalışmıyor olabilir):', text.substring(0, 200));
      throw new Error(`Backend API çalışmıyor. Lütfen backend'i başlatın: cd backend && npm start`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tables || [];
  } catch (error) {
    console.error('❌ Tablo listesi hatası:', error);
    throw error;
  }
};

/**
 * Backend API'den tablo verilerini çek
 */
export const getTableData = async (schema, tableName, limit = 100, offset = 0, search = '', columnFilters = {}) => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil. Lütfen .env.local dosyasına REACT_APP_API_BASE_URL ekleyin.');
    }

    const params = new URLSearchParams({
      schema: schema,
      table: tableName,
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (search.trim()) {
      params.set('search', search.trim());
    }
    const activeFilters = Object.fromEntries(Object.entries(columnFilters).filter(([, v]) => v && v.trim()));
    if (Object.keys(activeFilters).length > 0) {
      params.set('columnFilters', JSON.stringify(activeFilters));
    }

    const response = await fetch(`${API_BASE_URL}/api/database/data?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response'un JSON olup olmadığını kontrol et
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API HTML döndü (backend çalışmıyor olabilir):', text.substring(0, 200));
      throw new Error(`Backend API çalışmıyor. Lütfen backend'i başlatın: cd backend && npm start`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      rows: data.rows || [],
      columns: data.columns || [],
      totalCount: data.totalCount || 0,
    };
  } catch (error) {
    console.error('❌ Tablo verisi hatası:', error);
    throw error;
  }
};

/**
 * Backend API'den tablo yapısını (kolon bilgileri) çek
 */
export const getTableStructure = async (schema, tableName) => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil. Lütfen .env.local dosyasına REACT_APP_API_BASE_URL ekleyin.');
    }

    const params = new URLSearchParams({
      schema: schema,
      table: tableName,
    });

    const response = await fetch(`${API_BASE_URL}/api/database/structure?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response'un JSON olup olmadığını kontrol et
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API HTML döndü (backend çalışmıyor olabilir):', text.substring(0, 200));
      throw new Error(`Backend API çalışmıyor. Lütfen backend'i başlatın: cd backend && npm start`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.columns || [];
  } catch (error) {
    console.error('❌ Tablo yapısı hatası:', error);
    throw error;
  }
};

/**
 * Backend API'den SQL sorgusu çalıştır (sadece SELECT sorguları)
 */
export const executeQuery = async (query) => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil. Lütfen .env.local dosyasına REACT_APP_API_BASE_URL ekleyin.');
    }

    const trimmedQuery = query.trim().toUpperCase();
    if (!trimmedQuery.startsWith('SELECT')) {
      throw new Error('Sadece SELECT sorguları çalıştırılabilir.');
    }

    const response = await fetch(`${API_BASE_URL}/api/database/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ API HTML döndü (backend çalışmıyor olabilir):', text.substring(0, 200));
      throw new Error(`Backend API çalışmıyor. Lütfen backend'i başlatın: cd backend && npm start`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      rows: data.rows || [],
      columns: data.columns || [],
    };
  } catch (error) {
    console.error('❌ SQL sorgu hatası:', error);
    throw error;
  }
};

/**
 * Backend API'den yazma sorgusu çalıştır (INSERT/UPDATE/DELETE)
 */
export const executeWriteQuery = async (query) => {
  try {
    if (!API_BASE_URL) {
      throw new Error('Backend API endpoint tanımlı değil.');
    }

    const trimmedQuery = query.trim().toUpperCase();
    const allowed = ['INSERT', 'UPDATE', 'DELETE', 'ALTER', 'CREATE', 'DROP', 'TRUNCATE'];
    const isAllowed = allowed.some(cmd => trimmedQuery.startsWith(cmd));
    if (!isAllowed) {
      throw new Error('Bu sorgu türü desteklenmiyor. İzin verilenler: ' + allowed.join(', '));
    }

    let response = await fetch(`${API_BASE_URL}/api/database/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });

    if (response.status === 404 || response.status === 403) {
      response = await fetch(`${API_BASE_URL}/api/database/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Backend API çalışmıyor.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      rowCount: data.rowCount || 0,
      message: data.message || 'Sorgu başarıyla çalıştırıldı',
    };
  } catch (error) {
    console.error('❌ Yazma sorgu hatası:', error);
    throw error;
  }
};

/**
 * Tek satır ekle
 */
export const insertRow = async (schema, tableName, rowData) => {
  const columns = Object.keys(rowData);
  const values = Object.values(rowData).map(v => {
    if (v === null || v === undefined) return 'NULL';
    if (typeof v === 'number') return v;
    return `'${String(v).replace(/'/g, "''")}'`;
  });
  const query = `INSERT INTO "${schema}"."${tableName}" (${columns.map(c => `"${c}"`).join(', ')}) VALUES (${values.join(', ')})`;
  return executeWriteQuery(query);
};

/**
 * Satır güncelle
 */
export const updateRow = async (schema, tableName, setData, whereCondition) => {
  const setClauses = Object.entries(setData).map(([col, val]) => {
    if (val === null || val === undefined) return `"${col}" = NULL`;
    if (typeof val === 'number') return `"${col}" = ${val}`;
    return `"${col}" = '${String(val).replace(/'/g, "''")}'`;
  });
  const query = `UPDATE "${schema}"."${tableName}" SET ${setClauses.join(', ')} WHERE ${whereCondition}`;
  return executeWriteQuery(query);
};

/**
 * Satır sil
 */
export const deleteRow = async (schema, tableName, whereCondition) => {
  const query = `DELETE FROM "${schema}"."${tableName}" WHERE ${whereCondition}`;
  return executeWriteQuery(query);
};

/**
 * İki tabloyu kıyasla - kolon yapıları ve kayıt sayıları
 */
export const compareTables = async (schema1, table1, schema2, table2) => {
  try {
    const [struct1, struct2, data1, data2] = await Promise.all([
      getTableStructure(schema1, table1),
      getTableStructure(schema2, table2),
      getTableData(schema1, table1, 1, 0),
      getTableData(schema2, table2, 1, 0),
    ]);

    const cols1 = struct1.map(c => c.column_name);
    const cols2 = struct2.map(c => c.column_name);
    const commonColumns = cols1.filter(c => cols2.includes(c));
    const onlyIn1 = cols1.filter(c => !cols2.includes(c));
    const onlyIn2 = cols2.filter(c => !cols1.includes(c));

    const typeMap1 = {};
    struct1.forEach(c => { typeMap1[c.column_name] = c.data_type; });
    const typeMap2 = {};
    struct2.forEach(c => { typeMap2[c.column_name] = c.data_type; });

    const typeMismatches = commonColumns.filter(c => typeMap1[c] !== typeMap2[c]).map(c => ({
      column: c,
      type1: typeMap1[c],
      type2: typeMap2[c],
    }));

    return {
      table1: { schema: schema1, name: table1, columns: struct1, rowCount: data1.totalCount },
      table2: { schema: schema2, name: table2, columns: struct2, rowCount: data2.totalCount },
      commonColumns,
      onlyInTable1: onlyIn1,
      onlyInTable2: onlyIn2,
      typeMismatches,
    };
  } catch (error) {
    console.error('❌ Tablo kıyaslama hatası:', error);
    throw error;
  }
};

/**
 * Veritabanı bağlantı durumunu kontrol et
 */
export const checkDatabaseConnection = async () => {
  try {
    if (!API_BASE_URL) {
      return { connected: false, error: 'Backend API endpoint tanımlı değil' };
    }

    const response = await fetch(`${API_BASE_URL}/api/database/schemas`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      return { connected: true };
    } else {
      return { connected: false, error: `HTTP ${response.status}` };
    }
  } catch (error) {
    return { connected: false, error: error.message };
  }
};

/**
 * Tüm şemalardaki toplam tablo sayısını al
 */
export const getTotalTableCount = async () => {
  try {
    const schemas = await listSchemas();
    let totalTables = 0;
    
    for (const schema of schemas) {
      try {
        const tables = await listTables(schema);
        totalTables += tables.length;
      } catch (err) {
        console.warn(`⚠️ Şema ${schema} için tablo sayısı alınamadı:`, err.message);
      }
    }
    
    return totalTables;
  } catch (error) {
    console.error('❌ Toplam tablo sayısı hatası:', error);
    return 0;
  }
};

/**
 * Veritabanı istatistiklerini al
 */
export const getDatabaseStats = async () => {
  try {
    const connection = await checkDatabaseConnection();
    const totalTables = await getTotalTableCount();
    const schemas = await listSchemas();
    
    return {
      connected: connection.connected,
      totalSchemas: schemas.length,
      totalTables: totalTables,
      error: connection.error,
    };
  } catch (error) {
    console.error('❌ Veritabanı istatistikleri hatası:', error);
    return {
      connected: false,
      totalSchemas: 0,
      totalTables: 0,
      error: error.message,
    };
  }
};

export const databaseService = {
  listSchemas,
  listTables,
  getTableData,
  getTableStructure,
  executeQuery,
  executeWriteQuery,
  insertRow,
  updateRow,
  deleteRow,
  compareTables,
  checkDatabaseConnection,
  getTotalTableCount,
  getDatabaseStats,
};

